import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import RequestPtoModal from './RequestPtoModal';
import PrintPtoDecisionModal from './PrintPtoDecisionModal';
import ReviewPtoModal from './ReviewPtoModal';
import TruncatedText from './TruncatedText';
import StyledDate from './StyledDate';
import './Salon.css';

const REQUESTS_PER_PAGE = 20;

// created_at comes back as a full ISO timestamp; read local date parts rather than
// splitting the UTC string, which can land on the wrong calendar day depending on timezone.
const localDateOnly = (isoTimestamp) => {
  if (!isoTimestamp) return '';
  const date = new Date(isoTimestamp);
  if (isNaN(date.getTime())) return '';
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
};

const MyPtoTab = ({ salonId, userRole }) => {
  const { t, formatWeddingDate } = useLanguage();
  const isAdmin = userRole === 'owner' || userRole === 'admin';

  const [members, setMembers] = useState([]);
  // 'all' (admin default - every employee's requests at once) or a specific member's id.
  const [selectedUserId, setSelectedUserId] = useState('all');
  const [allocatedDays, setAllocatedDays] = useState(null);
  const [carriedOverDays, setCarriedOverDays] = useState(0);
  const [availableDays, setAvailableDays] = useState(null);
  const [pendingDays, setPendingDays] = useState(0);
  const [requests, setRequests] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [printTarget, setPrintTarget] = useState(null);
  const [reviewTarget, setReviewTarget] = useState(null); // { request, decision }

  // Admins/owners can switch between employees, same as the Time Sheet tab. PTO visibility
  // isn't role-restricted the way timesheets are, so every member (including the owner) is listed.
  // selectedUserId defaults to 'all' (see useState above), so no need to set it here.
  useEffect(() => {
    if (!isAdmin) return;
    const fetchMembers = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`/api/salons/${salonId}/members`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setMembers(response.data.members);
      } catch (err) {
        console.error('Error fetching salon members:', err);
      }
    };
    fetchMembers();
  }, [salonId, isAdmin]);

  const isViewingAll = isAdmin && selectedUserId === 'all';
  const isViewingSelf = !isViewingAll && (!isAdmin || selectedUserId === null || !!members.find((m) => m.id === selectedUserId)?.isCurrentUser);
  const selectedMember = members.find((m) => m.id === selectedUserId);
  // In the "all employees" view each row can belong to a different person, so the print target's
  // employee has to come from that specific request's own user_id rather than a single selection.
  const printTargetMember = isViewingAll
    ? members.find((m) => m.id === printTarget?.user_id)
    : isViewingSelf ? members.find((m) => m.isCurrentUser) : selectedMember;

  // An admin can approve/reject anyone else's request but not their own (which can still show up
  // as a row here in the "All Employees" view) - only an owner may review a request they filed
  // themselves. Mirrors the same rule enforced server-side in the review endpoint.
  const myMemberId = members.find((m) => m.isCurrentUser)?.id;
  const canReviewRequest = (req) => userRole === 'owner' || req.user_id !== myMemberId;

  // reviewer_first_name/last_name are only present when the request came from the admin
  // "view others" endpoint (it joins them); when viewing your own requests, look the reviewer
  // up in the members list by id instead - falls back to '' if they've since left the salon.
  const getApproverName = (req) => {
    if (!req) return '';
    if (req.reviewer_first_name) return `${req.reviewer_first_name} ${req.reviewer_last_name || ''}`.trim();
    const reviewer = members.find((m) => m.id === req.reviewed_by);
    return reviewer ? `${reviewer.first_name} ${reviewer.last_name}`.trim() : '';
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (isViewingAll) {
        // No single balance makes sense across every employee at once - just the combined list.
        const requestsRes = await axios.get(`/api/salons/${salonId}/pto/requests/all`, config);
        const sortedRequests = [...requestsRes.data.ptoRequests].sort((a, b) => b.start_date.localeCompare(a.start_date));
        setRequests(sortedRequests);
        setCurrentPage(1);
        setError('');
        return;
      }

      const [balanceRes, requestsRes] = await Promise.all([
        axios.get(`/api/salons/${salonId}/pto/balance`, { ...config, params: isViewingSelf ? {} : { userId: selectedUserId } }),
        isViewingSelf
          ? axios.get(`/api/salons/${salonId}/pto/requests`, config)
          : axios.get(`/api/salons/${salonId}/pto/requests/all`, { ...config, params: { userId: selectedUserId } })
      ]);
      setAllocatedDays(balanceRes.data.allocatedDays);
      setCarriedOverDays(balanceRes.data.carriedOverDays || 0);
      setAvailableDays(balanceRes.data.availableDays);
      setPendingDays(balanceRes.data.pendingDays);
      const sortedRequests = [...requestsRes.data.ptoRequests].sort((a, b) => b.start_date.localeCompare(a.start_date));
      setRequests(sortedRequests);
      setCurrentPage(1);
      setError('');
    } catch (err) {
      console.error('Error fetching PTO data:', err);
      setError(t('timeOff.pto.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [salonId, isViewingAll, isViewingSelf, selectedUserId, t]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSubmitted = () => {
    fetchData();
    setIsModalOpen(false);
  };

  const handleReviewed = () => {
    setReviewTarget(null);
    fetchData();
  };

  const totalPages = Math.ceil(requests.length / REQUESTS_PER_PAGE);
  const paginatedRequests = requests.slice((currentPage - 1) * REQUESTS_PER_PAGE, currentPage * REQUESTS_PER_PAGE);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(
        <button key={i} className={`pagination-btn ${currentPage === i ? 'active' : ''}`} onClick={() => setCurrentPage(i)}>
          {i}
        </button>
      );
    }
    return (
      <div className="pagination">
        <button className="pagination-btn" onClick={() => setCurrentPage(p => p - 1)} disabled={currentPage === 1}>
          {t('common.previous')}
        </button>
        {pages}
        <button className="pagination-btn" onClick={() => setCurrentPage(p => p + 1)} disabled={currentPage === totalPages}>
          {t('common.next')}
        </button>
      </div>
    );
  };

  // reviewer_first_name/last_name are only present when an admin is viewing someone else's
  // requests (the /pto/requests/all endpoint joins them in) - the "own requests" endpoint
  // doesn't, so fall back to a tooltip without the reviewer's name in that case.
  const buildApprovedTooltip = (req) => {
    if (!req.reviewed_at) return undefined;
    const date = formatWeddingDate(localDateOnly(req.reviewed_at));
    const reviewerName = req.reviewer_first_name ? `${req.reviewer_first_name} ${req.reviewer_last_name}`.trim() : '';
    return reviewerName
      ? t('timeOff.pto.approvedOnByTooltip', undefined, { date, reviewer: reviewerName })
      : t('timeOff.pto.approvedOnTooltip', undefined, { date });
  };

  const handleCancel = async (requestId) => {
    if (!window.confirm(t('messages.confirm.cancelPtoRequest'))) {
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`/api/salons/${salonId}/pto/requests/${requestId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (err) {
      console.error('Error cancelling PTO request:', err);
      alert(err.response?.data?.error || t('timeOff.pto.failedToCancel'));
    }
  };

  return (
    <div>
      <div className="pto-balance-card">
        {!isViewingAll && (
          <>
            <div className="pto-balance-item">
              <span className="pto-balance-value">{allocatedDays === null ? t('timeOff.pto.notSet') : allocatedDays}</span>
              <span className="pto-balance-label">{t('timeOff.pto.allocatedDays')}</span>
            </div>
            <div className="pto-balance-item">
              <span className="pto-balance-value">{carriedOverDays}</span>
              <span className="pto-balance-label">{t('timeOff.pto.carriedOverDays')}</span>
            </div>
            <div className="pto-balance-item">
              <span className="pto-balance-value">{availableDays === null ? t('timeOff.pto.notSet') : availableDays}</span>
              <span className="pto-balance-label">{t('timeOff.pto.daysAvailable')}</span>
            </div>
            <div className="pto-balance-item">
              <span className="pto-balance-value">{pendingDays}</span>
              <span className="pto-balance-label">{t('timeOff.pto.daysPending')}</span>
            </div>
          </>
        )}
        {isAdmin && members.length > 0 && (
          <select
            className="time-off-select pto-balance-employee-select"
            value={selectedUserId || ''}
            onChange={(e) => setSelectedUserId(e.target.value === 'all' ? 'all' : Number(e.target.value))}
          >
            <option value="all">{t('timeOff.pto.allEmployees')}</option>
            {members.map((m) => (
              <option key={m.id} value={m.id}>
                {m.isCurrentUser ? t('timeOff.hours.me') : `${m.first_name} ${m.last_name}`}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="table-header">
        <h4>{isViewingAll ? t('timeOff.pto.allRequests') : isViewingSelf ? t('timeOff.pto.myRequests') : `${selectedMember?.first_name} ${selectedMember?.last_name}`}</h4>
        {isViewingSelf && (
          <button className="salon-button primary" onClick={() => setIsModalOpen(true)}>
            {t('timeOff.pto.requestPto')}
          </button>
        )}
      </div>

      {loading ? (
        <div className="loading">{t('common.loading')}...</div>
      ) : error ? (
        <div className="error">{error}</div>
      ) : requests.length === 0 ? (
        <div className="table-empty-state">
          <div className="table-empty-state-icon">🌴</div>
          <h4>{t('timeOff.pto.noRequests')}</h4>
        </div>
      ) : (
        <div className="table-container">
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>{t('timeOff.pto.requested')}</th>
                  {isViewingAll && <th>{t('timeOff.pto.employee')}</th>}
                  <th>{t('timeOff.pto.startDate')}</th>
                  <th>{t('timeOff.pto.endDate')}</th>
                  <th>{t('timeOff.pto.days')}</th>
                  <th>{t('timeOff.pto.reason')}</th>
                  <th>{t('common.status')}</th>
                  <th>{t('timeOff.pto.reviewNote')}</th>
                  {(isViewingSelf || isAdmin) && <th>{t('common.actions')}</th>}
                </tr>
              </thead>
              <tbody>
                {paginatedRequests.map((req) => (
                  <tr key={req.id}>
                    <td><StyledDate date={localDateOnly(req.created_at)} showWeekday={false} /></td>
                    {isViewingAll && <td>{req.first_name} {req.last_name}</td>}
                    <td><StyledDate date={req.start_date} /></td>
                    <td><StyledDate date={req.end_date} /></td>
                    <td>{Math.round(Number(req.days_count))}</td>
                    <td><TruncatedText text={req.reason} label={t('timeOff.pto.reason')} /></td>
                    <td>
                      <span
                        className={`status-pill status-${req.status}`}
                        title={req.status === 'approved' ? buildApprovedTooltip(req) : undefined}
                      >
                        {t(`timeOff.pto.status.${req.status}`)}
                      </span>
                    </td>
                    <td><TruncatedText text={req.review_note} label={t('timeOff.pto.reviewNote')} /></td>
                    {(isViewingSelf || isAdmin) && (
                      <td>
                        <div className="table-actions">
                          {isViewingSelf && req.status === 'pending' && (
                            <button className="action-button delete" onClick={() => handleCancel(req.id)}>
                              {t('timeOff.pto.cancelRequest')}
                            </button>
                          )}
                          {isAdmin && !isViewingSelf && req.status === 'pending' && (
                            canReviewRequest(req) ? (
                              <>
                                <button className="action-button edit" onClick={() => setReviewTarget({ request: req, decision: 'approved' })}>
                                  {t('timeOff.pto.approve')}
                                </button>
                                <button className="action-button delete" onClick={() => setReviewTarget({ request: req, decision: 'rejected' })}>
                                  {t('timeOff.pto.reject')}
                                </button>
                              </>
                            ) : (
                              <span className="table-actions-note" title={t('timeOff.pto.cannotSelfApprove')}>
                                {t('timeOff.pto.cannotSelfApprove')}
                              </span>
                            )
                          )}
                          {isAdmin && req.status === 'approved' && (
                            <button className="action-button edit" onClick={() => setPrintTarget(req)}>
                              {t('timeOff.pto.printDecision')}
                            </button>
                          )}
                        </div>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="pagination-container">
            {renderPagination()}
          </div>
        </div>
      )}

      {isViewingSelf && (
        <RequestPtoModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          salonId={salonId}
          onSubmitted={handleSubmitted}
        />
      )}

      {isAdmin && (
        <PrintPtoDecisionModal
          isOpen={!!printTarget}
          onClose={() => setPrintTarget(null)}
          salonId={salonId}
          request={printTarget}
          employee={printTargetMember}
          approverName={getApproverName(printTarget)}
        />
      )}

      {isAdmin && (
        <ReviewPtoModal
          isOpen={!!reviewTarget}
          onClose={() => setReviewTarget(null)}
          salonId={salonId}
          request={reviewTarget?.request}
          decision={reviewTarget?.decision}
          onReviewed={handleReviewed}
        />
      )}
    </div>
  );
};

export default MyPtoTab;
